<template>
  <div class="near">
    <div ref="chart" class="map"></div>
    <div class="text">
      <h2 class="h1">{{ title }}</h2>
    </div>
    <div class="legend">
      <div class="legend-title" @click="clearStatusFilter" title="点击显示全部">
        工单状态
        <span v-if="activeStatus" class="legend-all">全部</span>
      </div>
      <div
        class="legend-item"
        v-for="item in legendList"
        :key="item.code"
        :class="{
          active: activeStatus === item.code,
          dimmed: activeStatus && activeStatus !== item.code,
        }"
        @click.stop="onLegendClick(item.code)"
      >
        <span class="dot" :style="{ background: item.color }"></span>
        <span>{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import "echarts/map/js/china";
import { WORK_ORDER_STATUS, statusLabel, urgencyLabel } from "@/common/js/workOrderDict";

/** 工单状态 → 标注颜色（与图二圆形徽章类似，按状态区分） */
const STATUS_MARKER_COLOR = {
  PENDING_ASSIGN: "#909399",
  PENDING_ACCEPT: "#ffa940",
  IN_REPAIR: "#00e5ff",
  PENDING_CONFIRM: "#c39bd3",
  COMPLETED: "#2ecc71",
  CANCELLED: "#7f8c8d",
};

function markerColor(status) {
  return STATUS_MARKER_COLOR[status] || "#00e5ff";
}

export default {
  name: "bdMap",
  props: {
    type: String,
    list: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      title: this.type === "robot" ? "工单分布图" : "项目分布",
      chart: null,
      activeStatus: null,
      legendList: Object.keys(WORK_ORDER_STATUS).map((code) => ({
        code,
        label: WORK_ORDER_STATUS[code],
        color: STATUS_MARKER_COLOR[code],
      })),
    };
  },
  watch: {
    list: {
      deep: true,
      handler() {
        this.renderChart();
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart();
      this.renderChart();
    });
    this._onResize = () => {
      if (this.chart) this.chart.resize();
    };
    window.addEventListener("resize", this._onResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this._onResize);
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  },
  methods: {
    onLegendClick(code) {
      // 再点一次同一状态 → 恢复全部
      this.activeStatus = this.activeStatus === code ? null : code;
      this.renderChart();
    },
    clearStatusFilter() {
      if (!this.activeStatus) return;
      this.activeStatus = null;
      this.renderChart();
    },
    initChart() {
      if (!this.$refs.chart) return;
      if (this.chart) this.chart.dispose();
      this.chart = echarts.init(this.$refs.chart);
      this.chart.on("click", (params) => {
        if (params.componentType === "series" && params.data) {
          this.$emit("marker-click", params.data.raw || params.data);
        }
      });
    },
    buildPointData() {
      const src = Array.isArray(this.list) ? this.list : [];
      const valid = src.filter(
        (item) =>
          item &&
          item.position &&
          item.position.lng != null &&
          item.position.lat != null &&
          (!this.activeStatus || item.workOrderStatus === this.activeStatus)
      );

      // 同坐标 + 同状态合并计数，避免互相完全遮盖导致“状态看不到”
      const groupMap = {};
      valid.forEach((item, index) => {
        const lng = Number(item.position.lng);
        const lat = Number(item.position.lat);
        const status = item.workOrderStatus || "";
        const locKey = `${lng.toFixed(3)}_${lat.toFixed(3)}`;
        const key = `${locKey}__${status}`;
        if (!groupMap[key]) {
          groupMap[key] = {
            locKey,
            lng,
            lat,
            status,
            count: 0,
            items: [],
            first: item,
            index,
          };
        }
        groupMap[key].count += 1;
        groupMap[key].items.push(item);
      });

      // 同一地点若有多种状态，按角度微偏移，保证各类状态点都能看见
      const locGroups = {};
      Object.keys(groupMap).forEach((key) => {
        const g = groupMap[key];
        if (!locGroups[g.locKey]) locGroups[g.locKey] = [];
        locGroups[g.locKey].push(g);
      });

      const OFFSET_DEG = 0.18; // 约十几公里量级，地图缩放后仍可区分
      const points = [];
      Object.keys(locGroups).forEach((locKey) => {
        const groups = locGroups[locKey];
        groups.forEach((g, i) => {
          let lng = g.lng;
          let lat = g.lat;
          if (groups.length > 1) {
            const angle = (2 * Math.PI * i) / groups.length - Math.PI / 2;
            lng = g.lng + Math.cos(angle) * OFFSET_DEG;
            lat = g.lat + Math.sin(angle) * OFFSET_DEG;
          }
          const status = g.status;
          const color = markerColor(status);
          const count = g.count;
          const raw = {
            ...g.first,
            workOrderStatus: status,
            _groupCount: count,
            _groupItems: g.items,
            info: {
              ...(g.first.info || {}),
              name:
                (g.first.info && g.first.info.name) ||
                `工单:${g.first.title || g.first.id || "-"}`,
              status: `状态:${statusLabel(status)} × ${count}`,
            },
          };
          points.push({
            name: g.first.title || g.first.id || `WO-${g.index + 1}`,
            value: [lng, lat, count],
            workOrderStatus: status,
            raw,
            itemStyle: {
              color,
              borderColor: "#fff",
              borderWidth: 2,
              shadowBlur: 10,
              shadowColor: color,
            },
            label: {
              show: true,
              formatter: String(count),
              color: "#fff",
              fontSize: count > 9 ? 11 : 12,
              fontWeight: 700,
            },
          });
        });
      });
      return points;
    },
    renderChart() {
      if (!this.chart) this.initChart();
      if (!this.chart) return;
      const points = this.buildPointData();
      this.chart.setOption(
        {
          backgroundColor: "transparent",
          tooltip: {
            trigger: "item",
            backgroundColor: "rgba(4, 20, 40, 0.92)",
            borderColor: "#00e5ff",
            borderWidth: 1,
            textStyle: { color: "#e8f4ff", fontSize: 12 },
            formatter: (params) => {
              if (params.seriesType !== "scatter" && params.seriesType !== "effectScatter") {
                return params.name || "";
              }
              const raw = (params.data && params.data.raw) || {};
              const info = raw.info || {};
              const status = statusLabel(raw.workOrderStatus);
              const urgency = urgencyLabel(raw.urgencyLevel);
              return [
                `<div style="font-weight:600;color:#00e5ff;margin-bottom:4px;">${info.name || params.name}</div>`,
                `<div>${info.status || `状态:${status} | ${urgency}`}</div>`,
                `<div>${info.address || "-"}</div>`,
              ].join("");
            },
          },
          geo: {
            map: "china",
            roam: true,
            zoom: 2.15,
            center: [105, 36],
            scaleLimit: { min: 0.8, max: 8 },
            label: {
              show: true,
              color: "rgba(200, 230, 255, 0.72)",
              fontSize: 10,
            },
            itemStyle: {
              areaColor: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: "rgba(12, 48, 92, 0.92)" },
                  { offset: 1, color: "rgba(6, 28, 58, 0.95)" },
                ],
              },
              borderColor: "#00e5ff",
              borderWidth: 1.2,
              shadowColor: "rgba(0, 229, 255, 0.35)",
              shadowBlur: 12,
            },
            emphasis: {
              label: {
                show: true,
                color: "#ffffff",
                fontWeight: 600,
              },
              itemStyle: {
                areaColor: "rgba(0, 180, 220, 0.72)",
                borderColor: "#58e2f1",
                borderWidth: 1.6,
                shadowColor: "rgba(0, 229, 255, 0.55)",
                shadowBlur: 16,
              },
            },
            regions: [
              {
                name: "南海诸岛",
                itemStyle: {
                  areaColor: "rgba(8, 36, 72, 0.9)",
                  borderColor: "#00e5ff",
                },
                label: { show: false },
              },
            ],
          },
          series: [
            {
              name: "工单",
              type: "scatter",
              coordinateSystem: "geo",
              zlevel: 2,
              data: points,
              symbol: "circle",
              symbolSize: (val) => {
                const n = (val && val[2]) || 1;
                return Math.min(42, 26 + Math.log2(n + 1) * 6);
              },
              label: {
                show: true,
                formatter: (params) => {
                  const n =
                    (params.data && params.data.value && params.data.value[2]) ||
                    1;
                  return String(n);
                },
                color: "#fff",
                fontSize: 12,
                fontWeight: 700,
              },
              emphasis: {
                scale: 1.15,
                label: {
                  show: true,
                  fontSize: 13,
                },
              },
            },
            {
              name: "工单光晕",
              type: "effectScatter",
              coordinateSystem: "geo",
              zlevel: 1,
              data: points.map((p) => ({
                ...p,
                itemStyle: {
                  color: (p.itemStyle && p.itemStyle.color) || "#00e5ff",
                  opacity: 0.35,
                },
                label: { show: false },
              })),
              symbolSize: (val) => {
                const n = (val && val[2]) || 1;
                return Math.min(48, 32 + Math.log2(n + 1) * 6);
              },
              showEffectOn: "render",
              rippleEffect: {
                brushType: "stroke",
                scale: 2.2,
                period: 4,
              },
              hoverAnimation: false,
            },
          ],
        },
        true
      );
      this.$nextTick(() => {
        if (this.chart) this.chart.resize();
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "../aftersaleOverview/css/screen-theme.scss";

.near {
  @include screen-tech-frame;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(ellipse at 50% 40%, rgba(0, 80, 140, 0.25) 0%, transparent 55%),
    #040b1a;

  &::before {
    content: "";
    position: absolute;
    top: -1px;
    left: -1px;
    width: 18px;
    height: 18px;
    border-top: 2px solid $screen-cyan;
    border-left: 2px solid $screen-cyan;
    box-shadow: -1px -1px 8px $screen-cyan-glow;
    z-index: 3;
    pointer-events: none;
  }

  &::after {
    content: "";
    position: absolute;
    top: -1px;
    right: -1px;
    width: 18px;
    height: 18px;
    border-top: 2px solid $screen-cyan;
    border-right: 2px solid $screen-cyan;
    box-shadow: 1px -1px 8px $screen-cyan-glow;
    z-index: 3;
    pointer-events: none;
  }
}

.map {
  width: 100%;
  height: 100%;
}

.text {
  min-width: 100px;
  background: linear-gradient(135deg, rgba(0, 140, 180, 0.85), rgba(0, 60, 100, 0.9));
  border: 1px solid rgba(0, 229, 255, 0.45);
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  box-sizing: border-box;
  padding: 6px 10px;
  text-align: center;
  box-shadow: 0 0 12px rgba(0, 200, 255, 0.3);

  .h1 {
    padding: 0;
    margin: 0;
    font-size: 15px;
    color: #fff;
    text-shadow: 0 0 6px rgba(0, 229, 255, 0.5);
  }
}

.legend {
  position: absolute;
  right: 10px;
  bottom: 10px;
  z-index: 5;
  max-width: calc(100% - 20px);
  padding: 8px 10px;
  background: rgba(4, 18, 36, 0.88);
  border: 1px solid rgba(0, 229, 255, 0.4);
  box-shadow: 0 0 10px rgba(0, 180, 255, 0.2);
  color: #d6ecff;
  font-size: 11px;
  line-height: 1.35;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
  row-gap: 4px;
}

.legend-title {
  grid-column: 1 / -1;
  color: #00e5ff;
  font-weight: 600;
  margin-bottom: 2px;
  letter-spacing: 1px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;

  .legend-all {
    font-size: 10px;
    font-weight: 500;
    color: #ffa940;
    letter-spacing: 0;
  }
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 3px;
  user-select: none;
  transition: opacity 0.15s, background 0.15s;

  &:hover {
    background: rgba(0, 229, 255, 0.12);
  }

  &.active {
    background: rgba(0, 229, 255, 0.22);
    box-shadow: inset 0 0 0 1px rgba(0, 229, 255, 0.45);
  }

  &.dimmed {
    opacity: 0.35;
  }
}

.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 0 6px currentColor;
  flex-shrink: 0;
}
</style>
